/**
 * "F2F login needed" (06-10-2026) en "F2F-sessie verloopt binnenkort"
 * (07-10-2026, Jordy).
 *
 * Rood: de worker meldt dat de bewaarde F2F-sessie ontbreekt of verlopen is;
 * F2F-chats, -posting en F2F Mass DM's liggen stil tot iemand opnieuw inlogt.
 * Oranje: de sessie verloopt binnen de drempel uit src/services/f2f_bewaking.py
 * (2 dagen); op tijd opnieuw inloggen voorkomt de rode stand.
 * Stappenplan: docs/operations/f2f-handmatig-inloggen.md (F2F-INLOGGEN).
 * Server: `GET conversations/f2f-login-status`.
 */
import { useApi, usePoll } from '../lib/useApi'
import { alsAmsterdam } from '../lib/amsterdam'
import { Link } from 'react-router-dom'
import { isVolledigBeheer, useMij } from '../lib/rechten'

interface Stand {
  needed: boolean
  since: string | null
  reason: string | null
  expires_at?: string | null
  expires_soon?: boolean
}

export function F2fLoginMelding() {
  const q = useApi<Stand>('/api/admin/conversations/f2f-login-status')
  usePoll(60000, q.refetch)
  const mij = useMij()
  // 07-10-2026: owner/admin krijgen een directe link naar de pagina F2F inloggen.
  const link = isVolledigBeheer(mij.data)
    ? <Link to="/f2f-login" className="btn sm" data-testid="f2f-inloggen-link">Log in to F2F</Link>
    : null
  const d = q.data
  if (!d) return null
  // 30-10-2026 (Jordy): de instructie ("an owner must log in ...") en de link
  // naar F2F login alleen voor owner/admin. Andere rollen zien een neutrale
  // melding zonder details; de server stuurt hun ook geen tijdstip of reden.
  // Ook terwijl de rol nog laadt: eerst de neutrale tekst.
  if (d.needed && !isVolledigBeheer(mij.data)) {
    return (
      <div className="openbar u-bad" role="alert" data-testid="f2f-niet-beschikbaar" style={{ marginBottom: 12 }}>
        <b>F2F chats temporarily unavailable</b>
        <span className="mut2">The owner is aware and is working on it.</span>
      </div>
    )
  }
  if (d.needed) {
    return (
      <div className="openbar u-bad" role="alert" data-testid="f2f-login-needed" style={{ marginBottom: 12 }}>
        <b>F2F login needed</b>
        <span className="mut2">
          F2F chats, posting and Mass DMs are paused{d.since ? ` since ${alsAmsterdam(d.since)}` : ''}.
          An owner must log in to F2F once on the server (page F2F login).
        </span>
        {link}
      </div>
    )
  }
  // 18-10-2026 (Jordy): de oranje melding alleen voor admin-accounts, niet
  // voor chatters (de server stuurt hem hun ook niet meer).
  if (d.expires_soon && isVolledigBeheer(mij.data)) {
    return (
      <div className="openbar u-warn" role="status" data-testid="f2f-sessie-verloopt" style={{ marginBottom: 12 }}>
        <b>F2F session expires soon</b>
        <span className="mut2">
          {d.expires_at ? `Valid until ${alsAmsterdam(d.expires_at)}. ` : 'No expiry date found. '}
          Log in to F2F again before then (page F2F login), otherwise F2F stops.
        </span>
        {link}
      </div>
    )
  }
  return null
}
